import { Permission, Role, UserInfo } from './../../../../store/session/session.store';
import { ToastrService } from 'ngx-toastr';
import {
  OemDealerService,
  VehicleUpdateStatus,
} from './../../../services/oem-dealer.service';
import { Router } from '@angular/router';
import {
  Component,
  OnInit,
  input,
  output,
  ChangeDetectorRef,
  NgZone,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import { SlugifyPipe } from 'ngx-pipes';
import {
  EvaluationVehicle,
  buildEvaluationStatus,
  StatusEvaluation,
  ReasonsArchivesLabel,
  NegotiationStatusTypes,
  EvaluationDestination,
} from '@app-model/evaluation';
import { TimeAgoPipe } from 'ngx-moment';
import * as stringFormat from 'string-format';
import { StringUtils } from '@app-utils/StringUtils';
import { CountDownService, CountDownTime } from '@app-services/count-down.service';
import { OpenService } from '@app-services/open.service';
import { Observable } from 'rxjs';
import { SessionService } from '@app/store/session/session.service';
import { DatePipe } from '@angular/common';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { EvaluationFillProgressBarComponent } from '@app-components/evaluation/evaluation-vehicle-detail/evaluation-fill-progress-bar/evaluation-fill-progress-bar.component';
import { MatTooltip } from "@angular/material/tooltip";
import { FipeComparisonBadgeComponent } from '@app-components/fipe-comparison-badge/fipe-comparison-badge.component';

@Component({
  selector: '[app-evaluation-card-vehicle]',
  standalone: true,
  imports: [CommonModule, RouterModule, EvaluationFillProgressBarComponent, MatTooltip, FipeComparisonBadgeComponent],
  templateUrl: './evaluation-card-vehicle.component.html',
  styleUrls: ['./evaluation-card-vehicle.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EvaluationCardVehicleComponent implements OnInit {
  readonly vehicle = input.required<EvaluationVehicle>();
  readonly ownsVehicle = input<boolean>(false);
  readonly gridMode = input<boolean>(false);

  readonly clickEmitter = output<void>();

  slug = '';
  descriptionStatus = '';
  initials = '';
  contador$: Observable<CountDownTime> | null = null;
  imgIndex = 0;
  vehicleImages: string[] = [];

  readonly isTransfer = input<boolean>(false);

  /** Opcional: o pai pode passar. Caso contrário usa o usuário da sessão. */
  readonly userInfo = input<UserInfo | undefined>(undefined);

  /** Usuário resolvido: vindo do input ou da sessão (após loadUserInfo). */
  private _userInfoFromSession: UserInfo | null = null;
  get userInfoResolved(): UserInfo | null {
    return this._userInfoFromSession ?? this.userInfo() ?? null;
  }

  get destination(): boolean {
    const d = this.vehicle().destination;
    return d?.toUpperCase() === 'SHOWROOM' || d === EvaluationDestination.SHOWROOM;
  }

  get destinationLabel(): string {
    return EvaluationDestination[this.vehicle().destination];
  }

  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly ngZone = inject(NgZone);
  private readonly router = inject(Router);
  private readonly oemDealerService = inject(OemDealerService);
  private readonly openService = inject(OpenService);
  private readonly toast = inject(ToastrService);
  private readonly countDownService = inject(CountDownService);
  private readonly sessionService = inject(SessionService);

  ngOnInit(): void {
    this.loadUserInfo();
    this.buildDescriptionStatus();
    this.initializeCountdown();
    this.loadVehicleImages();
    this.generateSlug();
  }

  private loadUserInfo(): void {
    this.sessionService.getUser().subscribe((user) => {
      this._userInfoFromSession = user;
      this.cdRef.markForCheck();
    });
  }

  private buildDescriptionStatus(): void {
    const v = this.vehicle();
    let name = v.userNameLastEvalHistoryItem;

    if (!this.lastHistoryItem?.createdAt) return;

    const date = new DatePipe('pt-BR').transform(this.lastHistoryItem.createdAt, 'dd/MM/yyyy');
    const dateTime = new DatePipe('pt-BR').transform(this.lastHistoryItem.createdAt, 'HH:mm');
    const time = `${date} às ${dateTime}`;
    const evalStatus = buildEvaluationStatus(v, false);

    if (!evalStatus) return;

    name = this.resolveUserName(name);
    const scheduledDate = this.formatScheduledDate();

    this.descriptionStatus = stringFormat(evalStatus.text, { name, time, scheduledDate });
    this.descriptionStatus = this.descriptionStatus.replace('por proprietário', 'pelo proprietário');
    this.initials = StringUtils.getInitialsName(name);
  }

  private resolveUserName(name: string | undefined): string {
    const v = this.vehicle();
    if (name) return name;

    if (this.lastHistoryItem?.type === 'CREATED' || v.status === 'EVALUATING') {
      return v.createdOnline ? 'proprietário' : 'Integração API';
    }

    if (v.type === 'ONLINE' && v.status !== 'EXPIRED') {
      return 'proprietário';
    }

    return name ?? '';
  }

  private formatScheduledDate(): string | undefined {
    const v = this.vehicle();
    if (!v.scheduledDate) return undefined;
    const datePipe = new DatePipe('pt-BR');
    return datePipe.transform(v.scheduledDate, 'dd/MM/yyyy') ?? undefined;
  }

  private initializeCountdown(): void {
    const v = this.vehicle();
    if (v.expirationTimeLeft && v.expirationTimeLeft > 0) {
      this.contador$ = this.countDownService.countDownTime(v.expirationTimeLeft, 0.25);
    }
  }

  private loadVehicleImages(): void {
    const v = this.vehicle();
    if (v.imgUrls && v.imgUrls.length > 0) {
      this.vehicleImages.push(v.imgUrls[0]);
    }
  }

  private generateSlug(): void {
    const v = this.vehicle();
    const { makeName, trimName, manufactureYear, modelYear } = v;
    const slugText = `${makeName} ${trimName} ${manufactureYear} ${modelYear}`;
    this.slug = new SlugifyPipe().transform(slugText);
  }

  getSmallImageUrl(imgUrl: string): string {
    if (!imgUrl.includes('img.autoarremate.com.br')) {
      return imgUrl;
    }

    const lastDot = imgUrl.lastIndexOf('.');
    return imgUrl.substring(0, lastDot) + '_small' + imgUrl.substring(lastDot);
  }

  get lastHistoryItem() {
    return this.vehicle().lastHistoryItem;
  }

  get status() {
    return buildEvaluationStatus(this.vehicle(), false);
  }

  get label() {
    return buildEvaluationStatus(this.vehicle()).label;
  }
  get icon() {
    return buildEvaluationStatus(this.vehicle()).icon;
  }
  get class() {
    return buildEvaluationStatus(this.vehicle()).class;
  }

  get hasFastQuoteStats(): boolean {
    return !!this.vehicle().stats;
  }

  get showInFastQuoteInfo(): boolean {
    if (!this.hasFastQuoteStats) return false;
    const v = this.vehicle();
    if (v.status === StatusEvaluation.RETURNED_PRICING) return false;
    return !!v.inFastQuote
      || v.status === StatusEvaluation.FAST_QUOTE
      || v.status === StatusEvaluation.FAST_QUOTE_WITH_OFFER;
  }

  get showInFastQuoteIcon(): boolean {
    if (!this.hasFastQuoteStats) return false;
    const v = this.vehicle();
    if (v.status === StatusEvaluation.RETURNED_PRICING) return false;
    if (!v.inFastQuote) return false;
    return v.status !== StatusEvaluation.FAST_QUOTE
      && v.status !== StatusEvaluation.FAST_QUOTE_WITH_OFFER;
  }

  get showProgress(): boolean {
    const v = this.vehicle();
    return (
      v.status === StatusEvaluation.EVALUATING ||
      v.status === StatusEvaluation.RETURNED_EVAL
    );
  }

  get percentage() {
    const v = this.vehicle();
    if (!this.showInFastQuoteInfo) {
      return (v.evalPercentage ?? 0) * 100;
    }
    return 0;
  }

  get descriptionAlignCenter() {
    const showProgress = this.showProgress;
    return (
      !showProgress && this.vehicle().status !== StatusEvaluation.NEGOTIATION
    );
  }

  get showPrice(): boolean {
    const v = this.vehicle();
    const validStatuses = [StatusEvaluation.NEGOTIATION, StatusEvaluation.AWAITING_ENTRY];
    const hasRole = !!this.userInfoResolved?.hasRoles(
      Role.ADMIN,
      Role.OEM_DEALER_MANAGER,
      Role.SELLER_MANAGER,
      Role.DESK_BUY
    );
    const hasPermission = !!this.userInfoResolved?.hasAnyPermission(Permission.VIEW_PRICING_PRICE);
    
    return validStatuses.includes(v.status) && (hasRole || hasPermission);
  }

  get showComments(): boolean {
    return !!this.vehicle().lastHistoryItem?.comments;
  }

  get comments(): string | null {
    return this.vehicle().lastHistoryItem?.comments ?? null;
  }

  get initialUserComment(): string | null {
    const v = this.vehicle();
    if (!v.userNameLastEvalHistoryItem) return null;
    return StringUtils.getInitialsName(v.userNameLastEvalHistoryItem);
  }

  get dateComment(): string {
    return new TimeAgoPipe(this.cdRef, this.ngZone).transform(
      this.vehicle().lastHistoryItem?.createdAt
    );
  }

  get proposedPriceForFipeComparison(): number | undefined {
    const v = this.vehicle();
    if (v.status === 'AWAITING_ENTRY') {
      return v.agreedPrice || v.price;
    }
    return v.price;
  }

  canEvalute(): boolean {
    if (!this.userInfoResolved) return false;

    const hasRole = this.userInfoResolved.hasRoles(
      Role.SELLER,
      Role.SELLER_MANAGER,
      Role.EVALUATOR,
      Role.ADMIN,
      Role.DESK_BUY,
      Role.EVAL_DEALER_MANAGER,
      Role.SUPER_EVALUATOR
    );

    const hasPermission = this.userInfoResolved.hasAnyPermission(Permission.VIEW_EVALUATION_DETAILS);

    return hasRole || hasPermission;
  }

  toDetails(slug: string, id: string): string[] {
    return this.canEvalute()
      ? ['/captar/veiculos', slug, id]
      : ['/vender/ready-transfer/details', slug, id];
  }

  rolesTransfer(): boolean {
    if (!this.userInfoResolved) return false;

    return this.userInfoResolved.hasRoles(
      Role.ADMIN,
      Role.DESK_SELL,
      Role.OEM_DEALER_MANAGER,
      Role.REGISTERER,
      Role.LOCATION_MANAGER
    );
  }

  checkCarTransfer(): boolean {
    const v = this.vehicle();
    return (
      v.status === StatusEvaluation.BOUGHT &&
      v.destination === EvaluationDestination.TRADE_IN &&
      !v.tradeInVehicleId
    );
  }

  checkHasTradeIn(): boolean {
    return this.vehicle()?.hasTradeIn ?? true;
  }

  canTransfer(): boolean {
    return this.checkCarTransfer() && this.rolesTransfer() && this.checkHasTradeIn();
  }

  sendToTransfer = (): void => {
    this.oemDealerService.createFromEval(this.vehicle().id).subscribe({
      next: (resp) => this.handleTransferResponse(resp),
      error: () => this.toast.error('Erro ao enviar veículo para repasse.')
    });
  };

  private handleTransferResponse(resp: { success: boolean; id?: string; status?: string }): void {
    if (resp.success && resp.id) {
      this.openTransferInNewTab(resp.id);
      this.toast.success('Veículo enviado para o repasse com sucesso.');
      this.reloadCurrentRoute();
    } else if (resp.status) {
      this.toast.error(VehicleUpdateStatus[resp.status], '', {
        timeOut: 10000,
      });
    }
  }

  private openTransferInNewTab(vehicleId: string): void {
    const slug = this.openService.getVehicleSlug(this.vehicle());
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['vender', 'veiculos', slug, vehicleId])
    );
    window.open(url, '_blank');
  }

  getTradeInVehicleUrl(): string | null {
    const tradeInVehicleId = this.vehicle().tradeInVehicleId;
    if (!tradeInVehicleId) {
      return null;
    }

    const slug = this.openService.getVehicleSlug(this.vehicle());
    return this.router.serializeUrl(
      this.router.createUrlTree(['vender', 'veiculos', slug, tradeInVehicleId])
    );
  }

  private reloadCurrentRoute(): void {
    const currentUrl = this.router.url;
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate([currentUrl]);
    });
  }

  getReasonArchiveLabel(reason: string): string {
    return ReasonsArchivesLabel.getLabel(reason);
  }

  getStatusNegotiation(status: string): string {
    return NegotiationStatusTypes.getLabel(status);
  }

  nextImg(vehicle: EvaluationVehicle): void {
    if (this.vehicleImages.length <= 1) {
      this.vehicleImages = this.vehicle().imgUrls ?? [];
    }
    if (vehicle.imgUrls && this.imgIndex < vehicle.imgUrls.length - 1) {
      this.imgIndex++;
    }
  }

  prevImg(_vehicle: EvaluationVehicle): void {
    if (this.imgIndex > 0) {
      this.imgIndex--;
    }
  }

  /** Arrow para preservar this se passado a ng-template. */
  emitClick = (): void => {
    this.clickEmitter.emit();
  };

  hasPricingResponsible(): boolean {
    const v = this.vehicle();
    return v.status === StatusEvaluation.PRICING && !!v.pricingUserId;
  }

  getPricingResponsibleName(): string {
    return this.vehicle().pricingUserName ?? '';
  }

  getPricingResponsibleInitials(): string {
    const name = this.vehicle().pricingUserName;
    if (!name) return '';
    return StringUtils.getInitialsName(name);
  }

  hasFirstPricer(): boolean {
    const v = this.vehicle();
    return v.status === StatusEvaluation.RETURNED_PRICING && !!v.firstPricingUserName;
  }

  getFirstPricerName(): string {
    return this.vehicle().firstPricingUserName ?? '';
  }

  getFirstPricerInitials(): string {
    const name = this.vehicle().firstPricingUserName;
    if (!name) return '';
    return StringUtils.getInitialsName(name);
  }
}
